import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { get } from 'lodash'

import { useUserLoginMutation, useVerifyBackupCodeMutation } from '@client-hub/store/api/crs/react'
import ROUTES from '@client-hub/shared/constants/routes'
import MfaAuth, { MfaAuthContentType } from 'components/Mfa/Auth'
import { BackupCodeSchema } from 'schemas/mfa'
import { handleBackupCodeChange } from 'utils/mfa'
import { useMfaNavigateState, useRedirect } from 'hooks'
import { useTranslation } from 'react-i18next'
import { CRS_LOGIN_MUTATION_CACHE_KEY } from 'constants/auth'
import { useNotifications } from '@client-hub/shared/react/hooks/notifications'

const MfaLoginRecovery = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const notify = useNotifications()
  const { uid, from, isAllowedMfa } = useMfaNavigateState()
  const [verifyBackupCode, { isSuccess, error: verifyBackupCodeError }] = useVerifyBackupCodeMutation()
  const [, { reset }] = useUserLoginMutation({ fixedCacheKey: CRS_LOGIN_MUTATION_CACHE_KEY })

  useRedirect(!isAllowedMfa)

  const content: MfaAuthContentType = {
    showLogo: true,
    back: { name: t('routes.mfaAuth.global.backLogin'), url: ROUTES.LOGIN.BASE, callback: reset },
    placeholder: t('forms.mfaAuth.fields.recoveryCode.placeholder'),
    label: t('routes.mfaAuth.recovery.label'),
    switchButton: { name: t('routes.mfaAuth.recovery.switchText'), url: ROUTES.LOGIN.MFA.BASE },
  }

  useEffect(() => {
    isSuccess && navigate(from?.pathname ? from?.pathname : ROUTES.BASE, { replace: true })
  }, [navigate, isSuccess, from?.pathname])

  useEffect(() => {
    if (get(verifyBackupCodeError, 'status') === 400) {
      notify({ message: get(verifyBackupCodeError, 'data.backup_code'), variant: 'error' })
    } else if (get(verifyBackupCodeError, 'status') === 401) {
      notify({ message: get(verifyBackupCodeError, 'data.detail'), variant: 'error' })
    }
  }, [verifyBackupCodeError, notify])

  const onSubmit = useCallback((code: string) => verifyBackupCode({ uid, backup_code: code }), [uid, verifyBackupCode])

  if (!isAllowedMfa) return null

  return <MfaAuth content={content} onSubmit={onSubmit} onChange={handleBackupCodeChange} schema={BackupCodeSchema} />
}

export default MfaLoginRecovery
